import { useState, useEffect, useRef } from "react";
import {
  Button,
  Progress,
  Spinner,
  AlertDialog,
  Tooltip,
  IconButton,
} from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";
import { DownloadIcon } from "@radix-ui/react-icons";
import { CheckboxWithCount } from "./checkbox-with-count";
import {
  ExportCountsQueryParams,
  useLazyGetExportCountsQuery,
} from "@/shared/api/lod-api";
import { downloadAnnotationsExport } from "@/shared/api/helpers";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";
import { ANNOTATION_FILTER_KEYS } from "../hooks/useAnnotationFilters";

export interface ExportOptions {
  includeOrders: boolean;
  includeJourneys: boolean;
  includeTransactions: boolean;
  includeTrackPersistence: boolean;
  includeProximities: boolean;
}

export interface ExportProgress {
  percentage: number;
  isComplete?: boolean;
  message: string;
}

interface CollectionCounts {
  orders: number;
  journeys: number;
  transactions: number;
  trackPersistence: number;
  proximities: number;
}

const ProgressIndicator: React.FC<{
  progress: ExportProgress;
}> = ({ progress }) => {
  return (
    <div className="space-y-4">
      {progress.message}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            {progress.isComplete ? (
              <span className="text-green-500">✓</span>
            ) : null}
            <span className="font-medium">Progress</span>
          </div>
          <span className="text-muted-foreground">{progress.percentage}%</span>
        </div>

        <Progress
          value={progress.percentage}
          className={twMerge(progress.isComplete ? "[&_div]:bg-green-500" : "")}
        />
      </div>
    </div>
  );
};

export const ExportCollections = () => {
  const { getQuery } = useUrlQueries();

  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeOrders: true,
    includeJourneys: false,
    includeTransactions: false,
    includeTrackPersistence: false,
    includeProximities: false,
  });

  const [
    getExportCounts,
    {
      data: countsData,
      isLoading: isLoadingCounts,
      isFetching: isFetchingCounts,
      error: countsError,
    },
  ] = useLazyGetExportCountsQuery();

  const counts: CollectionCounts | null = countsData
    ? {
        orders: countsData.result.orders_count,
        journeys: countsData.result.journeys_count,
        transactions: countsData.result.transaction_annotations_count,
        trackPersistence: countsData.result.track_persistence_annotations_count,
        proximities: countsData.result.proximity_annotations_count,
      }
    : null;

  useEffect(() => {
    if (countsError) {
      const errorMessage =
        countsError instanceof Error
          ? countsError.message
          : "Failed to fetch counts";
      setErrorMessage(errorMessage);
    }
  }, [countsError]);

  const getExportParams = (): ExportCountsQueryParams => {
    const urlOrderId = getQuery(ANNOTATION_FILTER_KEYS.SEARCH);
    const urlSource = getQuery(ANNOTATION_FILTER_KEYS.ORDER_SOURCE);
    const urlAssignee = getQuery(ANNOTATION_FILTER_KEYS.ASSIGNEE);
    const startDate = getQuery(ANNOTATION_FILTER_KEYS.FROM_DATETIME);
    const endDate = getQuery(ANNOTATION_FILTER_KEYS.TO_DATETIME);
    const urlOrdering = getQuery(ANNOTATION_FILTER_KEYS.ORDERING);
    const urlStageJourneyAnnotation = getQuery(
      ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION
    );
    const urlStageTransactionAnnotation = getQuery(
      ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION
    );
    const urlStageTrackPersistenceAnnotation = getQuery(
      ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION
    );

    return {
      search: urlOrderId || undefined,
      order_source: urlSource === "all" ? undefined : urlSource,
      annotator_id: urlAssignee || undefined,
      from_datetime: startDate || undefined,
      to_datetime: endDate || undefined,
      ordering: urlOrdering || undefined,
      stage_journey_annotation: urlStageJourneyAnnotation || undefined,
      stage_transaction_annotation: urlStageTransactionAnnotation || undefined,
      stage_track_persistence_annotation:
        urlStageTrackPersistenceAnnotation || undefined,
    };
  };

  const handleExport = async () => {
    try {
      const exportParams = getExportParams();

      setErrorMessage(null);
      setIsExporting(true);
      setProgress({ percentage: 0, message: "Starting export..." });

      const requestBody = {
        ...exportParams,
        orders: exportOptions.includeOrders,
        journeys: exportOptions.includeJourneys,
        transaction_annotations: exportOptions.includeTransactions,
        proximity_annotations: exportOptions.includeProximities,
        track_persistence_annotations: exportOptions.includeTrackPersistence,
      };

      setProgress({ percentage: 50, message: "Requesting export..." });

      await downloadAnnotationsExport(requestBody);

      setProgress({
        percentage: 100,
        isComplete: true,
        message: "Export complete! Your download should start automatically.",
      });
      setIsExporting(false);
    } catch (err) {
      setProgress(null);
      setIsExporting(false);

      let message: string;
      if (err instanceof Error) {
        message = err.message;
      } else if (typeof err === "string") {
        message = err;
      } else {
        message = "An unknown error occurred";
      }

      setErrorMessage(message);
    }
  };

  const handleClose = () => {
    if (isExporting && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsOpen(false);
    setProgress(null);
    setErrorMessage(null);
    setIsExporting(false);
  };

  const handleOptionChange =
    (key: keyof ExportOptions) => (checked: boolean) => {
      setExportOptions((prev) => ({
        ...prev,
        [key]: checked,
      }));
    };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialog.Trigger>
        <Tooltip content="Export to JSON">
          <IconButton
            onClick={() => {
              setIsOpen(true);
              getExportCounts(getExportParams());
            }}
            size="1"
            variant="soft"
            color="gray"
            mr="1"
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </AlertDialog.Trigger>
      <AlertDialog.Content className="sm:max-w-md">
        <AlertDialog.Title>Export Collections</AlertDialog.Title>
        <AlertDialog.Description>
          Select the collections you want to export. Files will be downloaded as
          a ZIP archive.
          <div className="my-4 space-y-2">
            {isLoadingCounts || isFetchingCounts ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner className="h-4 w-4" />
                Calculating collection sizes...
              </div>
            ) : (
              <>
                <CheckboxWithCount
                  label="Orders"
                  count={counts?.orders ?? null}
                  checked={exportOptions.includeOrders}
                  onCheckedChange={handleOptionChange("includeOrders")}
                />
                <CheckboxWithCount
                  label="Journeys"
                  count={counts?.journeys ?? null}
                  checked={exportOptions.includeJourneys}
                  onCheckedChange={handleOptionChange("includeJourneys")}
                />
                <CheckboxWithCount
                  label="Transaction Annotations"
                  count={counts?.transactions ?? null}
                  checked={exportOptions.includeTransactions}
                  onCheckedChange={handleOptionChange("includeTransactions")}
                />
                <CheckboxWithCount
                  label="Track Persistence Annotations"
                  count={counts?.trackPersistence ?? null}
                  checked={exportOptions.includeTrackPersistence}
                  onCheckedChange={handleOptionChange(
                    "includeTrackPersistence"
                  )}
                />
                <CheckboxWithCount
                  label="Proximity Annotations"
                  count={counts?.proximities ?? null}
                  checked={exportOptions.includeProximities}
                  onCheckedChange={handleOptionChange("includeProximities")}
                />
              </>
            )}
          </div>
          {progress && <ProgressIndicator progress={progress} />}
          {errorMessage && (
            <div className="text-sm text-destructive mt-2">
              <span className="text-red-500">⚠</span> {errorMessage}
            </div>
          )}
        </AlertDialog.Description>
        <div className="mt-4 flex justify-between">
          {!isExporting ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={
                  !Object.values(exportOptions).some(Boolean) ||
                  isLoadingCounts ||
                  isFetchingCounts
                }
              >
                Export
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button disabled>
                <Spinner className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </Button>
            </>
          )}
        </div>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};
